import {
  Card,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  InputAdornment,
} from "@material-ui/core";
import { BaseTextFieldProps } from "@material-ui/core/TextField";
import { Clear, HighlightOff, MoreHoriz } from "@material-ui/icons";
import React, { ReactElement, useEffect, useRef, useState } from "react";
import {
  Button,
  Datagrid,
  FieldTitle,
  InputHelperText,
  Record,
  ResettableTextField,
  ResourceContextProvider,
  RowClickFunction,
  TextField as RaTextField,
  useCheckMinimumRequiredProps,
  useDataProvider,
} from "react-admin";
import resourceNames from "../../../common/data/resourceNames";
import { TenantOutputDto } from "../../../common/data/service-proxies";
import { useSearchFields } from "../../../common/hooks/useSearchFields";
import { CompactListBase } from "./CompactListBase";
import classes from "./DriverPicker.module.css";
import SimplePagination from "../data-grid-fields/SimplePagination";

interface Props extends BaseTextFieldProps {
  label?: string | ReactElement | false;
  resource?: string;
  onChange: React.ChangeEventHandler<HTMLInputElement>;
  touched?: boolean;
  error?: any;
  submitError?: any;
  helperText?: string;
  options?: any;
  onBlur?: React.FocusEventHandler<HTMLInputElement>;
  onFocus?: React.FocusEventHandler<HTMLInputElement>;
  checked?: boolean;
  multiple?: boolean;
  value?: any;
  onSelectedRecord?: (record: (TenantOutputDto & Record) | null) => void;
}

const TenantPickerComponent = (
  props: Props,
  _: React.Ref<HTMLInputElement>
) => {
  useCheckMinimumRequiredProps("TenantPicker", ["name"], props);
  const {
    label,
    options,
    resource,
    submitError,
    required,
    name,
    touched,
    error,
    helperText,
    onSelectedRecord,
    ...rest
  } = props;

  useSearchFields(undefined, ["name"], resourceNames.MASTER_TENANTS_VIEW);

  const [displayValue, setDisplayValue] = useState<string>("");
  const [dialogOpen, setDialogOpen] = useState<boolean>(false);
  const [searchText, setSearchText] = useState<string>("");

  const inputRef = useRef<HTMLInputElement>(null);
  const dataProvider = useDataProvider();

  useEffect(() => {
    if (!dataProvider) {
      return;
    }

    if (!rest.value) {
      return;
    }

    if (rest.value === "") {
      return;
    }

    if (displayValue !== "") {
      return;
    }

    dataProvider
      .getOne<TenantOutputDto & Record>(resourceNames.MASTER_TENANTS_VIEW, {
        id: rest.value,
      })
      .then((result) => {
        setDisplayValue(result.data.name!);
        onSelectedRecord?.(result.data);
      })
      .catch((_) => setSelectedId(""));
  }, [dataProvider, rest.value, displayValue, onSelectedRecord]);

  const handleFocus: React.FocusEventHandler<HTMLInputElement> = (event) => {
    if (rest.onFocus) {
      rest.onFocus(event);
    }

    if (displayValue === "") {
      handleDialogOpen();
      inputRef.current?.blur();
    }
  };

  const input = {
    onBlur: rest.onBlur,
    onChange: rest.onChange,
    onFocus: handleFocus,
    checked: rest.checked,
    multiple: rest.multiple,
  };

  const inputOptions = { ...options } || {};
  inputOptions.inputProps = { ...inputOptions.inputProps, readOnly: true };

  const getEndAdornment = () => {
    return (
      <InputAdornment position="end">
        <IconButton
          className={classes["clear-button"]}
          aria-label="Browse"
          title="Browse"
          disableRipple
          disabled={rest.disabled}
          onClick={() => handleDialogOpen()}>
          <MoreHoriz />
        </IconButton>
      </InputAdornment>
    );
  };

  const handleDialogClose = () => {
    setDialogOpen(false);
  };

  const handleDialogOpen = () => {
    setDialogOpen(true);
  };

  const setSelectedId = (id: number | string) => {
    const nativeInputValueSetter = Object.getOwnPropertyDescriptor(
      window.HTMLInputElement.prototype,
      "value"
    )?.set;
    nativeInputValueSetter?.call(inputRef.current, id);
    const inputEvent = new Event("input", { bubbles: true });
    inputRef.current?.dispatchEvent(inputEvent);
  };

  const handleResetClick = () => {
    setDisplayValue("");
    onSelectedRecord?.(null);
    setSelectedId("");
    handleDialogClose();
  };

  const handleRowClick: RowClickFunction = (id, basePath, record) => {
    setDisplayValue(record.name);
    onSelectedRecord?.(record as TenantOutputDto & Record);
    setSelectedId(id);
    handleDialogClose();
    return Promise.resolve("");
  };

  return (
    <>
      <ResettableTextField
        label={
          label !== "" &&
          label !== false && (
            <FieldTitle
              label={label ?? undefined}
              source={name}
              resource={resource}
              isRequired={required}
            />
          )
        }
        error={!!(touched && (error || submitError))}
        helperText={
          <InputHelperText
            touched={touched ?? false}
            error={error || submitError}
            helperText={helperText}
          />
        }
        {...inputOptions}
        {...sanitizeInputRestProps(rest)}
        InputProps={{
          endAdornment: getEndAdornment(),
        }}
        value={displayValue}
        inputRef={inputRef}
        {...input}
      />
      <Dialog
        fullWidth
        maxWidth="md"
        open={dialogOpen}
        onClose={handleDialogClose}>
        <ResourceContextProvider value={resourceNames.MASTER_TENANTS_VIEW}>
          <CompactListBase
            perPage={25}
            sort={{ field: "name", order: "asc" }}
            basePath="/"
            resource={resourceNames.MASTER_TENANTS_VIEW}
            filter={{ q: searchText }}>
            <DialogTitle>
              <div className={classes["dialog-title"]}>
                Select a customer
                <div className={classes["dialog-title-toolbar"]}>
                  <ResettableTextField
                    source="q"
                    label="Search"
                    variant="outlined"
                    margin="dense"
                    autoFocus
                    value={searchText}
                    onChange={(e) => setSearchText(e.target?.value ?? "")}
                    resettable
                  />
                  <IconButton onClick={handleDialogClose}>
                    <Clear />
                  </IconButton>
                </div>
              </div>
            </DialogTitle>
            <DialogContent className={classes["dialog-content"]}>
              <Card>
                <Datagrid hasBulkActions={false} rowClick={handleRowClick}>
                  <RaTextField source="name" label="Name" />
                </Datagrid>
              </Card>
            </DialogContent>
            <DialogActions>
              <div className={classes["dialog-actions"]}>
                <Button label="Reset selection" onClick={handleResetClick}>
                  <HighlightOff />
                </Button>
                <SimplePagination rowsPerPageOptions={[25, 50, 100]} />
              </div>
            </DialogActions>
          </CompactListBase>
        </ResourceContextProvider>
      </Dialog>
    </>
  );
};

const sanitizeInputRestProps = ({
  afterSubmit,
  allowEmpty,
  allowNull,
  alwaysOn,
  basePath,
  beforeSubmit,
  component,
  data,
  defaultValue,
  error,
  format,
  formatOnBlur,
  formClassName,
  initialValue,
  initializeForm,
  input,
  isEqual,
  isRequired,
  label,
  limitChoicesToValue,
  locale,
  meta,
  multiple,
  name,
  options,
  optionText,
  optionValue,
  parse,
  record,
  ref,
  render,
  resource,
  source,
  submitError,
  subscription,
  textAlign,
  translate,
  translateChoice,
  type,
  validate,
  validateFields,
  value,
  ...rest
}: any) => rest;

export const TenantPicker = React.forwardRef(TenantPickerComponent);
